import logoGestinet from 'assets/img/favicon.png'
import Menu from 'components/layout/Menu/Menu'
import { useUser } from 'contexts/UserContext'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { LuPanelLeftClose, LuPanelLeftOpen, LuX } from 'react-icons/lu'
import { isAdminRole } from 'utils/roles'

function Brand({ collapsed }) {
  return (
    <div className="flex items-center gap-2.5 px-1.5">
      <img src={logoGestinet} alt="Gestinet" className="h-8 w-8 flex-none" />
      {!collapsed && (
        <div className="flex flex-col leading-tight">
          <span className="text-[15px] font-semibold tracking-tight text-gray-900">GestioAI</span>
          <span className="text-xs text-gray-500">by Gestinet</span>
        </div>
      )}
    </div>
  )
}

Brand.propTypes = { collapsed: PropTypes.bool }
Brand.defaultProps = { collapsed: false }

function UserCard({ collapsed }) {
  const { user } = useUser()
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || ''
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
  // Clientes: su empresa. Admin: no pertenece a una empresa concreta.
  const subtitle = isAdminRole(user?.role) ? 'Gestinet' : (user?.company_name || user?.project_name || '')

  return (
    <div className={`flex items-center gap-2.5 rounded-xl border border-gray-200 p-2.5 ${collapsed ? 'justify-center' : ''}`} title={collapsed ? name : undefined}>
      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-brand-soft text-[13px] font-semibold text-brand">
        {initials || '·'}
      </div>
      {!collapsed && (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-gray-900">{name}</span>
          {subtitle && <span className="truncate text-xs text-gray-500">{subtitle}</span>}
        </div>
      )}
    </div>
  )
}

UserCard.propTypes = { collapsed: PropTypes.bool }
UserCard.defaultProps = { collapsed: false }

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { t } = useTranslation('layout')

  return (
    <>
      {/* Escritorio: fijo a la izquierda, plegable a solo iconos */}
      <aside
        aria-label="Barra lateral"
        className={`hidden md:flex flex-none flex-col gap-6 border-r border-gray-200 bg-white px-3 py-5 transition-[width] duration-150 ${collapsed ? 'w-[72px]' : 'w-64'}`}
      >
        <Brand collapsed={collapsed} />
        <div id="scrollable-sidebar" className="-mx-1 flex-1 overflow-y-auto px-1">
          <Menu collapsed={collapsed} />
        </div>
        <UserCard collapsed={collapsed} />
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? t('navbar.expand_menu') : t('navbar.collapse_menu')}
          title={collapsed ? t('navbar.expand_menu') : t('navbar.collapse_menu')}
          className={`flex h-9 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900 ${collapsed ? 'justify-center' : ''}`}
        >
          {collapsed ? <LuPanelLeftOpen size={18} aria-hidden="true" /> : <LuPanelLeftClose size={18} aria-hidden="true" />}
          {!collapsed && <span>{t('navbar.collapse_menu')}</span>}
        </button>
      </aside>

      {/* Móvil: panel superpuesto que se abre desde la barra superior */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label={t('navbar.collapse_menu')}
            className="absolute inset-0 h-full w-full bg-gray-900/40"
            onClick={() => setMobileOpen(false)}
          />
          <aside aria-label="Barra lateral" className="relative flex h-full w-72 max-w-[85vw] flex-col gap-6 bg-white px-3 py-5 shadow-xl">
            <div className="flex items-center justify-between">
              <Brand />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label={t('navbar.collapse_menu')}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-50"
              >
                <LuX size={20} aria-hidden="true" />
              </button>
            </div>
            <div className="-mx-1 flex-1 overflow-y-auto px-1">
              <Menu onNavigate={() => setMobileOpen(false)} />
            </div>
            <UserCard />
          </aside>
        </div>
      )}
    </>
  )
}

Sidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  setCollapsed: PropTypes.func.isRequired,
  mobileOpen: PropTypes.bool.isRequired,
  setMobileOpen: PropTypes.func.isRequired,
}
